import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { SUPPLIER_PORTAL_PERMISSION } from 'src/app/home/module-logistics/procurement/supplier-portal/supplier-portal.paths';
import { AuthSnapshotService } from 'src/app/store/auth/auth-snapshot.service';

/**
 * Gates the supplier portal, which only an email-invited guest session may enter.
 *
 * Like authGuard this is not an authorization check — the token is the server's to accept or
 * reject, and a 401 is handled by ErrorHandlerService. It only keeps the portal from rendering
 * with an empty or wrong-kind session, where the header would show "Guest Access" and every
 * request would 401.
 */
export const guestGuard: CanActivateFn = (route, state): boolean | UrlTree => {
  const snap = inject(AuthSnapshotService);
  const router = inject(Router);

  if (snap.kind === 'guest' && !!snap.token && snap.can(SUPPLIER_PORTAL_PERMISSION)) return true;

  // A staff member who opened a supplier link, or a guest invited to something else, still has
  // a live session. guest-login forwards any signed-in visitor straight back to returnUrl, so
  // sending them there would loop; the no-access page ends the session first.
  if (snap.kind !== 'none' && !!snap.token) {
    return router.createUrlTree(['/no-access'], { queryParams: { login: 'guest', returnUrl: state.url } });
  }

  // Nobody signed in: the attempted URL rides along so token sign-in lands on the page they opened.
  return router.createUrlTree(['/guest-login'], { queryParams: { returnUrl: state.url } });
};
